import { useState, useEffect } from "react";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Package, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CONSULTATION_URL } from "@/data/constants";
import { trackConversion } from "@/hooks/useSiteAnalytics";
import useSEO, { breadcrumbSchema } from "@/hooks/useSEO";

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  company: "",
  role_title: "",
  product_interest: [],
  intended_use: "",
  estimated_volume: "",
  target_launch: "",
  ship_address_line1: "",
  ship_address_line2: "",
  ship_city: "",
  ship_state: "",
  ship_postal_code: "",
  ship_country: "United States",
  notes: "",
};

const SampleRequest = () => {
  useSEO({
    title: "Request Samples — FLOWTALIS™",
    description: "Order pre-configured sample kits from FLOWTALIS™. Purified water, alkaline, sparkling, CBD, and functional beverage samples shipped to qualified brands.",
    canonical: "https://flowtalis.com/request-samples",
    jsonLd: breadcrumbSchema([
      { label: "Home", href: "/" },
      { label: "Request Samples", href: "/request-samples" },
    ]),
  });
  const [form, setForm] = useState(emptyForm);
  const [products, setProducts] = useState([]);
  const [kits, setKits] = useState([]);
  const [selectedKit, setSelectedKit] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.products || [])).catch(() => {});
    api.get("/sample-kits").then((r) => setKits(r.data.kits || [])).catch(() => {});
  }, []);

  const applyKit = (kit) => {
    setSelectedKit(kit.slug);
    setForm((f) => ({ ...f, product_interest: [...kit.product_slugs] }));
    trackConversion("kit_selected", {
      kit_name: kit.name,
      kit_slug: kit.slug,
      kit_ribbon: kit.ribbon_text || undefined,
      product_count: kit.product_slugs.length,
    });
  };

  const toggleProduct = (slug) => {
    setForm((f) => ({
      ...f,
      product_interest: f.product_interest.includes(slug)
        ? f.product_interest.filter((s) => s !== slug)
        : [...f.product_interest, slug],
    }));
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.product_interest.length === 0) {
      toast.error("Please select at least one product of interest.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/sample-requests", {
        ...form,
        source_page: "sample-request-center",
      });
      trackConversion("sample_request_submit", {
        product_count: form.product_interest.length,
        products: form.product_interest.join(","),
        kit_name: selectedKit || undefined,
        country: form.ship_country || undefined,
        estimated_volume: form.estimated_volume || undefined,
        target_launch: form.target_launch || undefined,
      });
      setSubmitted(true);
      toast.success("Sample request submitted. Our team will review shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <PublicLayout>
        <section className="ft-section-dark py-24">
          <div className="ft-container max-w-2xl ft-fade-in relative text-center">
            <CheckCircle2 size={56} style={{ color: "var(--ft-gold)" }} className="mx-auto mb-8" />
            <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-4">
              Request Received
            </div>
            <h1 className="text-white text-[clamp(2rem,4vw,3rem)] font-bold tracking-tight leading-[1.05] mb-6">
              Your sample request is <span className="ft-serif text-[var(--ft-gold)]">under review</span>.
            </h1>
            <p className="text-white/60 text-lg leading-relaxed mb-10">
              A FLOWTALIS specialist will review your qualification details and reach out within one
              business day. Approved requests are shipped from our nearest facility.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link to="/" className="ft-btn-ghost">Return Home</Link>
              <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold" data-testid="sample-success-book">
                Book a Consultation <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="ft-section-dark py-20">
        <div className="ft-container max-w-3xl ft-fade-in relative">
          <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">
            Sample Request Center
          </div>
          <span className="ft-gold-rule-lg" />
          <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-6">
            Request a <span className="ft-serif text-[var(--ft-gold)]">FLOWTALIS sample kit</span>.
          </h1>
          <p className="text-white/60 text-lg leading-relaxed">
            To preserve product integrity and manage responsible fulfillment, every sample request
            is reviewed by a FLOWTALIS specialist. Please share a few qualification details below —
            you&apos;ll receive an approval decision within one business day.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="ft-container max-w-3xl">
          <form onSubmit={submit} className="ft-card space-y-6" data-testid="sample-request-form">
            <div>
              <div className="ft-eyebrow mb-2">Step 01 · Contact</div>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <label className="ft-label">Full Name*</label>
                  <input required className="ft-input" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="sr-full-name" />
                </div>
                <div>
                  <label className="ft-label">Email*</label>
                  <input required type="email" className="ft-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="sr-email" />
                </div>
                <div>
                  <label className="ft-label">Phone</label>
                  <input className="ft-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="sr-phone" />
                </div>
                <div>
                  <label className="ft-label">Company / Brand</label>
                  <input className="ft-input" value={form.company} onChange={(e) => update("company", e.target.value)} data-testid="sr-company" />
                </div>
                <div className="md:col-span-2">
                  <label className="ft-label">Role / Title</label>
                  <input className="ft-input" placeholder="Founder, Head of Product, etc." value={form.role_title} onChange={(e) => update("role_title", e.target.value)} data-testid="sr-role" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--ft-border)]">
              <div className="ft-eyebrow mb-2">Step 02 · Products*</div>
              <div className="text-xs text-[var(--ft-muted)] mb-4">
                Select the FLOWTALIS product(s) you&apos;d like to sample, or start from a prebuilt Sample Kit below.
              </div>

              {kits.length > 0 && (
                <div className="mb-5" data-testid="sr-kits">
                  <div className="text-[0.6875rem] uppercase tracking-[0.24em] text-[var(--ft-gold)] font-semibold mb-2">Sample Kits</div>
                  <div className="flex flex-wrap gap-2">
                  {kits.map((k) => (
                    <button
                      type="button"
                      key={k.slug}
                      onClick={() => applyKit(k)}
                      data-testid={`sr-kit-${k.slug}`}
                      className={`relative text-xs px-4 py-2 rounded-full border transition-all ${
                        selectedKit === k.slug
                          ? "bg-[var(--ft-gold)] text-[var(--ft-black)] border-[var(--ft-gold)]"
                          : "border-[var(--ft-border)] hover:border-[var(--ft-gold)] hover:text-[var(--ft-gold)]"
                      }`}
                    >
                      {k.ribbon_text && (
                        <span
                          className="absolute -top-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-[0.55rem] font-bold uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[var(--ft-black)] text-[var(--ft-gold)] border border-[var(--ft-gold)]"
                          data-testid={`sr-kit-ribbon-${k.slug}`}
                        >
                          {k.ribbon_text}
                        </span>
                      )}
                      {k.name} <span className="opacity-70">({k.product_slugs.length})</span>
                    </button>
                  ))}
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-3" data-testid="sr-product-grid">
                {products.map((p) => {
                  const selected = form.product_interest.includes(p.slug);
                  return (
                    <button
                      type="button"
                      key={p.slug}
                      onClick={() => toggleProduct(p.slug)}
                      data-testid={`sr-product-${p.slug}`}
                      className={`text-left rounded-xl p-4 border transition-all ${
                        selected
                          ? "border-[var(--ft-gold)] bg-[var(--ft-gold)]/8 shadow-[0_0_0_2px_rgba(200,162,74,0.15)]"
                          : "border-[var(--ft-border)] hover:border-[var(--ft-charcoal)]/40"
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Package size={14} className={selected ? "text-[var(--ft-gold)]" : "text-[var(--ft-muted)]"} />
                        <div className="font-semibold text-sm text-[var(--ft-black)]">{p.name}</div>
                      </div>
                      <div className="text-xs text-[var(--ft-muted)] leading-relaxed">{p.tagline}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--ft-border)]">
              <div className="ft-eyebrow mb-2">Step 03 · Qualification</div>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="ft-label">Intended Use*</label>
                  <textarea required rows={3} placeholder="Retail launch, hospitality program, corporate gift, brand activation…" className="ft-input" value={form.intended_use} onChange={(e) => update("intended_use", e.target.value)} data-testid="sr-intended-use" />
                </div>
                <div>
                  <label className="ft-label">Estimated Order Volume</label>
                  <input placeholder="e.g. 5,000 cases per month" className="ft-input" value={form.estimated_volume} onChange={(e) => update("estimated_volume", e.target.value)} data-testid="sr-volume" />
                </div>
                <div>
                  <label className="ft-label">Target Launch</label>
                  <input placeholder="e.g. Q3 2026" className="ft-input" value={form.target_launch} onChange={(e) => update("target_launch", e.target.value)} data-testid="sr-launch" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--ft-border)]">
              <div className="ft-eyebrow mb-2">Step 04 · Shipping Address</div>
              <div className="grid md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="ft-label">Address Line 1</label>
                  <input className="ft-input" value={form.ship_address_line1} onChange={(e) => update("ship_address_line1", e.target.value)} data-testid="sr-addr1" />
                </div>
                <div className="md:col-span-2">
                  <label className="ft-label">Address Line 2</label>
                  <input className="ft-input" value={form.ship_address_line2} onChange={(e) => update("ship_address_line2", e.target.value)} data-testid="sr-addr2" />
                </div>
                <div>
                  <label className="ft-label">City</label>
                  <input className="ft-input" value={form.ship_city} onChange={(e) => update("ship_city", e.target.value)} data-testid="sr-city" />
                </div>
                <div>
                  <label className="ft-label">State / Region</label>
                  <input className="ft-input" value={form.ship_state} onChange={(e) => update("ship_state", e.target.value)} data-testid="sr-state" />
                </div>
                <div>
                  <label className="ft-label">Postal Code</label>
                  <input className="ft-input" value={form.ship_postal_code} onChange={(e) => update("ship_postal_code", e.target.value)} data-testid="sr-postal" />
                </div>
                <div>
                  <label className="ft-label">Country</label>
                  <input className="ft-input" value={form.ship_country} onChange={(e) => update("ship_country", e.target.value)} data-testid="sr-country" />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-[var(--ft-border)]">
              <label className="ft-label">Anything else we should know?</label>
              <textarea rows={3} className="ft-input" value={form.notes} onChange={(e) => update("notes", e.target.value)} data-testid="sr-notes" />
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-[var(--ft-border)] flex-wrap gap-3">
              <div className="text-xs text-[var(--ft-muted)] max-w-md">
                All sample requests are reviewed by a FLOWTALIS specialist before shipment. Approved
                requests are fulfilled at no cost to qualifying brands and distributors.
              </div>
              <button type="submit" disabled={loading} className="ft-btn-primary" data-testid="sr-submit">
                {loading ? "Submitting…" : "Submit for Review"}
              </button>
            </div>
          </form>
        </div>
      </section>
    </PublicLayout>
  );
};

export default SampleRequest;
